import { Alert, Button, LinearProgress, Stack } from "@mui/material";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { AccountChannelsSection } from "../../modules/ce-admin/components/account-channels-section";
import { AccountCustomDashboardSection } from "../../modules/ce-admin/components/account-custom-dashboard-section";
import { AccountDetailNav } from "../../modules/ce-admin/components/account-detail-nav";
import { AccountGeneralSection } from "../../modules/ce-admin/components/account-general-section";
import { AccountIntegrationsSection } from "../../modules/ce-admin/components/account-integrations-section";
import { AccountLimitsSection } from "../../modules/ce-admin/components/account-limits-section";
import { useAccountDetail } from "../../modules/ce-admin/hooks/use-account-detail";
import { useSaveAccountLimits } from "../../modules/ce-admin/hooks/use-save-account-limits";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { useFlag } from "../../server/optimizely";
import { CeAdminPageHeader } from "./ce-admin-page-header";

import type { AdminAccountLimit } from "../../modules/generic/server/supabase-account-service";
import type { UUID } from "../../utils/uuid";
import type { FC } from "react";

const AccountDetailPage: FC = () => {
  const { id } = useParams<{ id: UUID }>();
  const { data: loaded, loading, error } = useAccountDetail(id);
  const [customDashboardFeatureEnabled] = useFlag("custom_luzmo_dashboard");

  // Hold the account in local state so a section save can patch it in place
  // rather than reloading the page — reloading would unmount the other
  // sections and discard their unsaved edits. We adopt freshly-loaded data
  // (initial load and navigation between accounts) by comparing identity
  // during render, the React-sanctioned alternative to a syncing effect.
  const [account, setAccount] = useState<AdminAccountLimit | null>(loaded);
  const [syncedFrom, setSyncedFrom] = useState(loaded);
  if (loaded !== syncedFrom) {
    setSyncedFrom(loaded);
    setAccount(loaded);
  }

  const save = useSaveAccountLimits(account, setAccount);

  return (
    <Page title="Administration | ChangeEngine">
      <PageHeader display="flex">
        <PageHeading heading={<CeAdminPageHeader />} subheading={account?.name ?? "Account"} />
      </PageHeader>
      <PageContent>
        {loading && <LinearProgress />}
        {error && (
          <Alert
            severity="error"
            action={
              <Button
                component={Link}
                to="/ce_admin"
                color="inherit"
                size="small"
                data-analytics-id="back-to-accounts"
              >
                Back to accounts
              </Button>
            }
          >
            {error.message}
          </Alert>
        )}
        {account && (
          <Stack direction="row" spacing={3}>
            <AccountDetailNav customDashboard={customDashboardFeatureEnabled} />
            <Stack key={account.id} spacing={3} sx={{ flexGrow: 1, minWidth: 0 }}>
              <AccountGeneralSection account={account} onSave={save} />
              <AccountChannelsSection account={account} onSave={save} />
              <AccountLimitsSection account={account} onSave={save} />
              <AccountIntegrationsSection account={account} />
              {customDashboardFeatureEnabled && (
                <AccountCustomDashboardSection accountId={account.id} />
              )}
            </Stack>
          </Stack>
        )}
      </PageContent>
    </Page>
  );
};

export default AccountDetailPage;
